import { SubmitExamPaperErrorCode, useSubmitExam } from '@exam-room/graphql';
import { useTranslation } from '@exam-room/translations';
import { useDisclosure } from '@shared/hooks';
import { useCallback, useMemo, useRef } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { Header, Timer, TimerSkeleton } from '../../../components';
import {
  useAuth,
  useExamCard,
  useExamCardExpired,
  useNewExam,
  useNewExamForm,
  useNetwork,
  useExamEvent,
} from '../../../providers';
import { PATHS } from '../../../routes/constants';
import { ExamEndingSubmissionModal } from '../components';

export default function ExamHeader({
  menus,
  withTimer = true,
}: {
  menus: JSX.Element[];
  withTimer?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { examCardId } = useParams();
  const { user } = useAuth();
  const { isOnline } = useNetwork();

  const { loading, examTime } = useNewExam();
  const { examCard } = useExamCard();
  const { closestExam, examDuration } = useExamEvent();
  const examForm = useNewExamForm();

  const [
    isEndingSubmissionModalOpen,
    { open: openEndingSubmissionModal, close: closeEndingSubmissionModal },
  ] = useDisclosure(false);
  const [submitExam, { loading: isSubmitting }] = useSubmitExam();
  const { showExamCardExpiredModal } = useExamCardExpired();

  // Track the mode the modal was opened in (offline or online)
  // This persists even if network status changes while modal is open
  const modalOpenedOfflineRef = useRef(false);

  const handleOpenEndingSubmissionModal = useCallback(() => {
    // Capture the network status at the moment the modal is opened
    modalOpenedOfflineRef.current = !isOnline;
    openEndingSubmissionModal();
  }, [isOnline, openEndingSubmissionModal]);

  const handleCloseEndingSubmissionModal = useCallback(() => {
    // Reset the mode when modal closes
    modalOpenedOfflineRef.current = false;
    closeEndingSubmissionModal();
  }, [closeEndingSubmissionModal]);

  const handleSubmitExam = async () => {
    const submitExamInput = Object.entries(examForm.getValues()).map(([, value]) => {
      const typedValue = value;
      return {
        examPaperItemId: value.examPaperItemId,
        answer: { value: typedValue.answer ?? null },
      };
    });

    await submitExam({
      variables: {
        submitExamInput: {
          rawAnswers: submitExamInput,
        },
      },
      onCompleted: (data) => {
        if (data.erSubmitExamPaper.isSuccess && data.erSubmitExamPaper.examPaper) {
          navigate(generatePath(PATHS.Exam.Submitted, { examCardId }));
          handleCloseEndingSubmissionModal();
        } else if (data.erSubmitExamPaper.errorCode === SubmitExamPaperErrorCode.ExamCardExpired) {
          handleCloseEndingSubmissionModal();
          showExamCardExpiredModal();
        } else {
          alert('ไม่สามารถส่งคำตอบได้ กรุณาลองใหม่อีกครั้ง');
        }
      },
      onError: () => {
        // TODO: Handle error properly and i18n
        alert('ไม่สามารถส่งคำตอบได้ กรุณาลองใหม่อีกครั้ง');
      },
    });
  };

  const examStartedAt = useMemo(
    () => (examTime?.startedAt ? new Date(examTime.startedAt) : null),
    [examTime?.startedAt],
  );

  const adjustedExamEndedAt = useMemo(
    () => (examTime?.endedAt ? new Date(examTime.endedAt) : null),
    [examTime?.endedAt],
  );

  const displayedExamEndedAt = useMemo(
    () =>
      closestExam?.globalAdjustedEndDate
        ? new Date(
            closestExam.globalAdjustedEndDate.getTime() +
              (examDuration?.totalManualExtendedDurationInMs || 0),
          )
        : null,
    [closestExam?.globalAdjustedEndDate, examDuration?.totalManualExtendedDurationInMs],
  );

  return (
    <>
      <Header
        left={
          <div className='flex items-center gap-4'>
            <img src='/logo.svg' alt='logo' className='h-10' />
            <div className='text-text-primary text-body2 flex flex-col gap-0.5'>
              <div>
                {t('header.seatNumber')} {examCard.seatNumber ?? '-'}
              </div>
              <div className='line-clamp-1 font-semibold break-all'>
                {user?.displayName} {user?.idNumber}
              </div>
            </div>
          </div>
        }
        middle={
          withTimer &&
          (loading || !examStartedAt || !adjustedExamEndedAt || !displayedExamEndedAt ? (
            <div className='h-[65px]'>
              <TimerSkeleton className='h-8 w-16' />
            </div>
          ) : (
            <>
              <Timer
                startedAt={examStartedAt}
                adjustedEndedAt={adjustedExamEndedAt}
                displayedEndedAt={displayedExamEndedAt}
                totalManualExtendedDurationInMs={examDuration?.totalManualExtendedDurationInMs}
                onTimerEnd={handleOpenEndingSubmissionModal}
              />
              <ExamEndingSubmissionModal
                open={isEndingSubmissionModalOpen}
                onSubmitExam={handleSubmitExam}
                submitting={isSubmitting}
                isOnline={isOnline}
                openedOffline={modalOpenedOfflineRef.current}
              />
            </>
          ))
        }
        right={<div className='flex items-center gap-2'>{menus}</div>}
      />
    </>
  );
}
